<template>
  <div class="container-fluid py-4">

    <!-- Breadcrumb -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3>Médicaments</h3>
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb mb-0">
          <li class="breadcrumb-item">Pharmacie</li>
          <li class="breadcrumb-item active" aria-current="page">Médicaments</li>
        </ol>
      </nav>
    </div>

    <!-- Bloc statistiques -->
    <div class="row mb-4">
      <div class="col-md-3 mb-3" v-for="(item, index) in stats" :key="index">
        <div class="card text-white text-center p-3 shadow" :class="cardColors[index % cardColors.length]">
          <h5>{{ item.label }}</h5>
          <h4 class="fw-bold">{{ item.value }}</h4>
        </div>
      </div>
    </div>

    <!-- Boutons haut tableau -->
    <div class="d-flex justify-content-end gap-2 mb-3">
      <button class="btn btn-primary" @click="creation()" v-if="$store.getters.hasPermission('MEDICAMENT_CREER')">
        <i class="bi bi-plus-circle"></i> Nouveau
      </button>
      <div class="dropdown">
        <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
          Actions
        </button>
        <ul class="dropdown-menu">
          <li><a class="dropdown-item" href="#" @click.prevent="">Exporter</a></li>
        </ul>
      </div>
    </div>

    <!-- Filtres -->
    <div class="card p-3 mb-3 shadow-sm">
      <div class="row g-3">
        <div class="col-md-3">
          <label class="form-label">Médicament ciblé</label>
          <Multiselect
            v-model="selectedMedicament"
            :options="medicaments.map(m => ({ value: m.id, label: m.nom }))"
            placeholder="Rechercher un médicament"
            searchable
            clearable
          />
        </div>
        <div class="col-md-2">
          <label class="form-label">Items / page</label>
          <select v-model="itemsperpage" class="form-select">
            <option v-for="item in itemsPerPageT" :key="item" :value="item">{{ item }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Trier par</label>
          <select v-model="orderBy" class="form-select">
            <option v-for="(val, key) in orderByTabs" :key="key" :value="val">{{ key }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label">Ordre</label>
          <select v-model="sortOrder" class="form-select">
            <option value="asc">Croissant</option>
            <option value="desc">Décroissant</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="isLoading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status" style="width: 4rem; height: 4rem;"></div>
      <p class="mt-3">Chargement des médicaments...</p>
    </div>

    <!-- Tableau -->
    <div v-else class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-hover table-bordered align-middle text-center mb-0">
          <thead class="table-primary">
            <tr>
              <th>#</th>
              <th>Nom</th>
              <th>Unité</th>
              <th>Stock Actuel</th>
              <th>Prix Achat</th>
              <th>Prix Vente</th>
              <th>Total retiré</th>
              <th>Créé le</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(med, index) in filteredMedicaments" :key="med.id">
              <td>{{ index + 1 + (currentPage - 1) * itemsperpage }}</td>
              <td>{{ med.nom }}</td>
              <td>{{ med.unite || '-' }}</td>
              <td :style="{ backgroundColor: getStockColor(med.stockActuel), fontWeight: 'bold' }">
                {{ med.stockActuel }}
              </td>
              <td>{{ med.prixAchat.toLocaleString() }} FCFA</td>
              <td>{{ med.prixVente.toLocaleString() }} FCFA</td>
              <td>{{ med.totalRetire || 0 }}</td>
              <td>{{ formatDate(med.createdAt) }}</td>
              <td>
                <div class="dropdown">
                  <button class="btn btn-sm btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">-</button>
                  <ul class="dropdown-menu">
                    <li v-if="$store.getters.hasPermission('MEDICAMENT_DETAILS')"><a class="dropdown-item" @click="details(med.id)">Détails</a></li>
                    <li v-if="$store.getters.hasPermission('MEDICAMENT_MODIFICATION')"><a class="dropdown-item" @click="modifier(med.id)">Modifier</a></li>
                    <li v-if="$store.getters.hasPermission('MEDICAMENT_SUPRESSION')"><a class="dropdown-item text-danger" @click="supprimer(med.id)">Supprimer</a></li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr v-if="filteredMedicaments.length === 0">
              <td colspan="9" class="text-center">Aucun médicament trouvé</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <nav class="mt-3">
        <ul class="pagination justify-content-end flex-wrap">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="changePage(currentPage - 1)">Précédent</button>
          </li>
          <li v-for="page in totalPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
            <button class="page-link" @click="changePage(page)">{{ page }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="changePage(currentPage + 1)">Suivant</button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import dayjs from 'dayjs';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

export default {
  components: { Multiselect },
  data() {
    return {
      isLoading: true,
      searchQuery: '',
      selectedMedicament: null,
      itemsperpage: 10,
      currentPage: 1,
      orderBy: 'med.createdAt',
      sortOrder: 'desc',
      itemsPerPageT: [5, 10, 20],
      orderByTabs: { Nom: 'med.nom', Stock: 'med.stockActuel', Prix: 'med.prixVente', Date: 'med.createdAt' },
      medicaments: [],
      totalItems: 0,
      stats: [
        { label: 'Total Médicaments', value: '...' },
        { label: 'Valeur Totale (Achat)', value: '...' },
        { label: 'Valeur Totale (Vente)', value: '...' },
        { label: 'Montant Total Vendu', value: '0 FCFA' }
      ],
      cardColors: ['bg-primary', 'bg-success', 'bg-info', 'bg-secondary', 'bg-warning', 'bg-danger']
    };
  },
  computed: {
    totalPages() { return Math.ceil(this.totalItems / this.itemsperpage); },
    filteredMedicaments() {
      let result = this.medicaments.filter(m => !m.isDeleted);
      if (this.selectedMedicament) result = result.filter(m => m.id === this.selectedMedicament);
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        result = result.filter(m => m.nom.toLowerCase().includes(q) || (m.unite || '').toLowerCase().includes(q));
      }
      return result;
    }
  },
  methods: {
    formatDate(date) { return dayjs(date).format('DD-MM-YYYY HH:mm'); },
    changePage(page) { if (page > 0 && page <= this.totalPages) { this.currentPage = page; this.getMedicaments(); } },
    creation() { this.$router.push({ name: 'create_medicament' }); },
    modifier(id) { this.$router.push({ name: 'edit_medicament', params: { id } }); },
    details(id) { this.$router.push({ name: 'details', params: { id } }); },
    async supprimer(id) { if (confirm("Supprimer ce médicament ?")) { await api.softDeleteItem('medicaments', id); this.getMedicaments(); } },
    getStockColor(stock) {
      if (stock === 0) return '#FF4C4C';
      if (stock <= 5) return '#FFA500';
      if (stock <= 10) return '#FFEB3B';
      if (stock <= 30) return '#8BC34A';
      return '#4CAF50';
    },
    async getMedicaments() {
      this.isLoading = true;
      try {
        const response = await api.getItems('medicaments', { orderBy: this.orderBy, sortOrder: this.sortOrder, itemsperpage: this.itemsperpage, page: this.currentPage, search: this.searchQuery });
        const { data, meta } = response.data;
        this.medicaments = data.map(m => {
          const valids = (m.approvisionnements || []).filter(a => !a.isDeleted);
          const stock = valids.reduce((sum, a) => sum + a.quantite, 0);
          const last = valids.at(-1);
          return { ...m, stockActuel: stock, prixAchat: last?.prixAchat || 0, prixVente: last?.prixVente || 0 };
        });
        this.totalItems = meta?.total_records || data.length;
        this.updateStats(this.filteredMedicaments);
      } catch (err) { console.error('Erreur de chargement', err); }
      finally { this.isLoading = false; }
    },
    updateStats(data) {
      const totalPrixAchat = data.reduce((acc, m) => acc + (m.prixAchat || 0) * (m.stockActuel || 0), 0);
      const totalPrixVente = data.reduce((acc, m) => acc + (m.prixVente || 0) * (m.stockActuel || 0), 0);
      const montantTotalVendu = data.reduce((acc, m) => acc + (m.totalRetire || 0) * (m.prixVente || 0), 0);
      this.stats[0].value = data.length;
      this.stats[1].value = `${totalPrixAchat.toLocaleString()} FCFA`;
      this.stats[2].value = `${totalPrixVente.toLocaleString()} FCFA`;
      this.stats[3].value = `${montantTotalVendu.toLocaleString()} FCFA`;
    }
  },
  watch: {
    searchQuery() { this.currentPage = 1; this.getMedicaments(); },
    selectedMedicament() { this.updateStats(this.filteredMedicaments); },
    itemsperpage() { this.currentPage = 1; this.getMedicaments(); },
    orderBy() { this.getMedicaments(); },
    sortOrder() { this.getMedicaments(); }
  },
  created() { this.getMedicaments(); }
};
</script>

<style scoped>
.table th, .table td { text-align: center; vertical-align: middle; }
.breadcrumb { background: transparent; padding: 0; margin: 0; }
.pagination .page-item .page-link { color: #0d6efd; }
.pagination .page-item.active .page-link { background-color: #0d6efd; border-color: #0d6efd; color: white; }
.card { border-radius: 12px; }
</style>
